import { Callout, FieldGroup, FormSection, Icon, Radio, RadioGroup, ScrollBar, toastError, toastWarning } from 'admination-react'
import { useEffect, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'

import { applyCombinedUpload, NO_TB_WARNING, type CombinedUploadStage } from '../../../flows/avgs'
import { avgsLooksPlausible } from '../../../lib/avgsPlausible'
import { handleFailure } from '@/core/lib/toast'
import { UPLOAD_ACCEPT } from '@/core/lib/fileTypes'
import { DropCard } from '@/core/components/modal/DropCard'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { useTabGating } from '@/core/hooks/useTabGating'
import { AUFGABE } from '@/core/data/aufgabe'
import { getTbEinreichung, setTbEinreichung, type TbEinreichung } from '../../../data/coaching'

const STAGE_LABEL: Record<CombinedUploadStage, string> = {
  splitting: 'Seiten werden zusammengeführt …',
  classifying: 'Seiten werden zugeordnet …',
  storing: 'Dokumente werden gespeichert …',
  extracting: 'AVGS wird ausgelesen …',
}

export function AvgsUpload({ coachingId }: { coachingId: string | null }) {
  const { dispatch, reloadDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [loading, setLoading] = useState(false)
  const { position } = useTabGating()
  // null while the stored answer loads; the radios wait for it.
  const [einreichung, setEinreichung] = useState<TbEinreichung | null>(null)
  useEffect(() => {
    let cancelled = false
    setEinreichung(null)
    if (!coachingId) return
    getTbEinreichung(coachingId)
      .then((choice) => { if (!cancelled) setEinreichung(choice?.einreichung ?? 'digital') })
      .catch((e) => {
        console.error('Einreichungsweg konnte nicht geladen werden:', e)
        toastError('Einreichungsweg konnte nicht geladen werden. Bitte erneut öffnen.')
      })
    return () => { cancelled = true }
  }, [coachingId])
  const settled = position === null || position >= AUFGABE.tbHochladen.position
  const original = einreichung === 'original'

  async function choose(next: TbEinreichung) {
    if (!coachingId || next === einreichung) return
    const previous = einreichung
    setEinreichung(next)
    try {
      await setTbEinreichung(coachingId, next)
    } catch (e) {
      console.error('Einreichungsweg speichern fehlgeschlagen:', e)
      setEinreichung(previous)
      toastError('Einreichungsweg konnte nicht gespeichert werden. Bitte erneut versuchen.')
    }
  }
  const [stage, setStage] = useState<CombinedUploadStage | null>(null)

  async function onFiles(files: File[]) {
    if (!coachingId || files.length === 0) return
    setLoading(true)
    setStage(null)
    try {
      const result = await applyCombinedUpload(coachingId, files, setStage)
      if (!result.ok) {
        handleFailure(result)
        return
      }
      const warnings = result.data.warnings?.filter((w) => !original || w !== NO_TB_WARNING) ?? []
      if (warnings.length) {
        for (const w of warnings) toastWarning(w)
      } else if (!result.data.tbStored && !original) {
        toastWarning(
          'Nur eine Seite erkannt – als AVGS gespeichert. Die Trägerbestätigung fehlt noch.',
        )
      } else if (!avgsLooksPlausible(result.data.form)) {
        toastWarning(
          'Das AVGS-Dokument sieht nicht wie ein Gutschein aus – bitte im Korrekturformular prüfen.',
        )
      }
      reloadDashboard()
      dispatch({ type: "CHANGE_TAB", tab: 'avgs' })
    } catch (e) {
      console.error('AVGS/Trägerbestätigung-Upload fehlgeschlagen:', e)
      toastError('AVGS/Trägerbestätigung-Upload fehlgeschlagen. Bitte erneut versuchen.')
    } finally {
      setLoading(false)
      setStage(null)
    }
  }

  return (
    <>
      <ModalHeader size="form" onClose={() => dispatch({ type: 'CLOSE' })} />
      <main className="form" data-scroll-host ref={scrollerRef}>
        <fieldset className="form-contents" disabled={loading}>
          <FormSection label="Trägerbestätigung">
            <FieldGroup size="question" legend="Wie reichen Sie die Trägerbestätigung beim Kostenträger ein?">
              <RadioGroup stacked>
                <Radio name="tb-einreichung" value="digital" checked={einreichung === 'digital'} disabled={einreichung === null || settled} onChange={() => { void choose('digital') }}>
                  Admination füllt die Trägerbestätigung aus. Ich lade die leere Trägerbestätigung zusammen mit dem AVGS hoch.
                </Radio>
                <Radio name="tb-einreichung" value="original" checked={original} disabled={einreichung === null || settled} onChange={() => { void choose('original') }}>
                  Ich fülle das Original selbst aus und sende es an den Kostenträger. Ich lade nur das AVGS hoch und erfasse die Angaben danach.
                </Radio>
              </RadioGroup>
              <Callout tone="warning" role="alert" icon={<Icon name="i-bolt" />} title="Bei der Agentur für Arbeit gibt es nur den zweiten Weg:">
                Das Original muss vor Maßnahmebeginn bei der Agentur vorliegen.
              </Callout>
            </FieldGroup>
          </FormSection>
          <FormSection label="Upload">
            {original ? (
              <DropCard
                title="AVGS hier ablegen"
                body="PDF oder Bild hierher ziehen oder auswählen"
                accept={UPLOAD_ACCEPT}
                onFile={(file) => { void onFiles([file]) }}
                loading={loading}
                loadingLabel={stage ? STAGE_LABEL[stage] : 'Dokumente werden hochgeladen …'}
              />
            ) : (
              <DropCard
                title="AVGS und Trägerbestätigung hier ablegen"
                body="AVGS und leere Trägerbestätigung – als mehrseitige PDF oder mehrere Dateien/Fotos, die Reihenfolge ist egal – hierher ziehen oder auswählen"
                accept={UPLOAD_ACCEPT}
                multiple
                onFiles={onFiles}
                loading={loading}
                loadingLabel={stage ? STAGE_LABEL[stage] : 'Dokumente werden hochgeladen …'}
              />
            )}
          </FormSection>
        </fieldset>
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
